import { memo, useMemo } from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { type CohortForAdminListsAttrs } from 'Cohorts';
import { AUTOCOMPLETE_SX } from './filterStyles';
import { toArray } from './filterUtils';

interface CohortMultiSelectProps {
    /** All available cohorts (may be pre-filtered by program type) */
    cohorts: CohortForAdminListsAttrs[];
    /** Currently selected cohort IDs */
    value: string[] | undefined;
    /** Called when selection changes */
    onChange: (cohortIds: string[] | undefined) => void;
    /** Optional label override */
    label?: string;
    /** Optional minimum width override */
    minWidth?: number;
    /** Optional maximum width override */
    maxWidth?: number;
    /** Keep dropdown inside the DOM tree (required when used inside a modal with focus traps) */
    disablePortal?: boolean;
}

function CohortMultiSelectComponent({
    cohorts,
    value,
    onChange,
    label = 'Cohort',
    minWidth = 220,
    maxWidth = 350,
    disablePortal,
}: CohortMultiSelectProps) {
    // Sort cohorts by name
    const sortedCohorts = useMemo(() => [...cohorts].sort((a, b) => a.name.localeCompare(b.name)), [cohorts]);

    // Get selected cohort objects
    const selectedCohorts = useMemo(() => {
        const cohortIds = toArray(value);
        return sortedCohorts.filter(c => cohortIds.includes(c.id));
    }, [sortedCohorts, value]);

    return (
        <Autocomplete
            multiple
            size="small"
            disablePortal={disablePortal}
            sx={{ minWidth, maxWidth, ...AUTOCOMPLETE_SX }}
            options={sortedCohorts}
            getOptionLabel={c => c.name}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            value={selectedCohorts}
            onChange={(_, values) => {
                const cohortIds = values.map(v => v.id);
                onChange(cohortIds.length > 0 ? cohortIds : undefined);
            }}
            renderInput={params => <TextField {...params} label={label} />}
            renderValue={(values, getItemProps) =>
                values.map((cohort, index) => {
                    const { key, ...tagProps } = getItemProps({ index });
                    return <Chip key={key} label={cohort.name} size="small" {...tagProps} />;
                })
            }
        />
    );
}

export const CohortMultiSelect = memo(CohortMultiSelectComponent);
